import React, { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Store, Plus, Trash2, Save, Loader2, MapPin, Clock, Users, Image as ImageIcon, Calendar, X, Upload } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "./ImageUploader";

const DAYS = [
  { key: "mon", label: "Lun" },
  { key: "tue", label: "Mar" },
  { key: "wed", label: "Mer" },
  { key: "thu", label: "Jeu" },
  { key: "fri", label: "Ven" },
  { key: "sat", label: "Sam" },
  { key: "sun", label: "Dim" },
];

const newLocation = () => ({
  id: "loc_" + Math.random().toString(36).slice(2, 10),
  name: "",
  address: "",
  postal_code: "",
  city: "",
  country: "France",
  notes: "",
  active: true,
  hours: DAYS.reduce((acc, d) => ({ ...acc, [d.key]: d.key === "sun" ? [] : [{ open: "09:00", close: "18:00" }] }), {}),
  closed_days: [],
});

export default function PickupAdmin() {
  const { settings, refresh } = useSettings();
  const [s, setS] = useState(null);
  const [preparers, setPreparers] = useState([]);
  const [preparerEdit, setPreparerEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (settings) setS({ ...settings }); }, [settings]);
  const loadPreparers = async () => {
    try { setPreparers((await api.get("/preparers?include_inactive=true")).data); } catch {}
  };
  useEffect(() => { loadPreparers(); }, []);

  if (!s) return <div className="p-10"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const locations = s.pickup_locations || [];
  const updateLoc = (idx, patch) => {
    const next = [...locations];
    next[idx] = { ...next[idx], ...patch };
    setS({ ...s, pickup_locations: next });
  };
  const removeLoc = (idx) => {
    if (!window.confirm("Supprimer ce point de retrait ?")) return;
    setS({ ...s, pickup_locations: locations.filter((_, i) => i !== idx) });
  };
  const addLoc = () => setS({ ...s, pickup_locations: [...locations, newLocation()] });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        shop_click_collect_enabled: !!s.shop_click_collect_enabled,
        pickup_locations: locations,
        pickup_slot_duration_min: +s.pickup_slot_duration_min || 30,
        pickup_capacity_per_slot: +s.pickup_capacity_per_slot || 5,
        pickup_lead_hours: +s.pickup_lead_hours || 2,
        pickup_max_days_ahead: +s.pickup_max_days_ahead || 14,
      });
      toast.success("Configuration Click & Collect enregistrée");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  // --- Preparers ---
  const savePreparer = async () => {
    try {
      if (preparerEdit.id) await api.put(`/preparers/${preparerEdit.id}`, preparerEdit);
      else await api.post("/preparers", preparerEdit);
      toast.success("Préparateur enregistré");
      setPreparerEdit(null); loadPreparers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };
  const deletePreparer = async (id) => {
    if (!window.confirm("Supprimer ce préparateur ?")) return;
    await api.delete(`/preparers/${id}`);
    loadPreparers();
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="admin-pickup-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-bold mb-2">Boutique</div>
          <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)]">Click &amp; Collect</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Configurez vos points de retrait, créneaux et préparateurs. Les clients pourront choisir un créneau au moment du paiement.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold">Activer Click &amp; Collect</span>
          <Switch
            checked={!!s.shop_click_collect_enabled}
            onCheckedChange={v => setS({ ...s, shop_click_collect_enabled: v })}
            data-testid="pickup-enable-toggle"
          />
        </div>
      </header>

      {/* Règles globales */}
      <Card className="p-6 border space-y-4" data-testid="pickup-rules-card">
        <h3 className="font-display font-bold flex items-center gap-2"><Clock className="w-4 h-4" /> Règles globales des créneaux</h3>
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <Label>Durée / créneau (min)</Label>
            <Input type="number" min="10" max="120" value={s.pickup_slot_duration_min ?? 30}
              onChange={e => setS({ ...s, pickup_slot_duration_min: +e.target.value })} data-testid="slot-duration" />
          </div>
          <div>
            <Label>Capacité max / créneau</Label>
            <Input type="number" min="1" max="100" value={s.pickup_capacity_per_slot ?? 5}
              onChange={e => setS({ ...s, pickup_capacity_per_slot: +e.target.value })} data-testid="slot-capacity" />
          </div>
          <div>
            <Label>Délai minimum (h)</Label>
            <Input type="number" min="0" max="72" value={s.pickup_lead_hours ?? 2}
              onChange={e => setS({ ...s, pickup_lead_hours: +e.target.value })} data-testid="slot-lead" />
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Temps min entre commande et retrait.</p>
          </div>
          <div>
            <Label>Fenêtre réservation (jours)</Label>
            <Input type="number" min="1" max="60" value={s.pickup_max_days_ahead ?? 14}
              onChange={e => setS({ ...s, pickup_max_days_ahead: +e.target.value })} data-testid="slot-window" />
          </div>
        </div>
      </Card>

      {/* Points de retrait */}
      <Card className="p-6 border space-y-4" data-testid="pickup-locations-card">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold flex items-center gap-2"><Store className="w-4 h-4" /> Points de retrait ({locations.length})</h3>
          <Button onClick={addLoc} className="bg-[color:var(--brand-red)]" data-testid="add-location-btn">
            <Plus className="w-4 h-4 mr-1" />Nouveau point
          </Button>
        </div>
        {locations.length === 0 ? (
          <div className="p-10 border-dashed border-2 rounded-xl text-center">
            <MapPin className="w-8 h-8 mx-auto text-[hsl(var(--muted-foreground))]/40 mb-2" />
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Aucun point de retrait. Ajoutez votre premier lieu de collecte.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {locations.map((loc, idx) => (
              <LocationEditor key={loc.id} loc={loc} idx={idx} onChange={updateLoc} onRemove={removeLoc} />
            ))}
          </div>
        )}
      </Card>

      {/* Préparateurs */}
      <Card className="p-6 border space-y-4" data-testid="pickup-preparers-card">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold flex items-center gap-2"><Users className="w-4 h-4" /> Préparateurs de commande ({preparers.length})</h3>
          <Button onClick={() => setPreparerEdit({ first_name: "", last_name: "", avatar_url: "", role: "Préparateur", active: true })} className="bg-[color:var(--brand-navy)]" data-testid="add-preparer-btn">
            <Plus className="w-4 h-4 mr-1" />Nouveau préparateur
          </Button>
        </div>
        {preparers.length === 0 ? (
          <div className="p-8 border-dashed border-2 rounded-xl text-center text-sm text-[hsl(var(--muted-foreground))]">
            Aucun préparateur. Ajoutez au moins une personne pour pouvoir l'assigner aux commandes.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {preparers.map(p => (
              <Card key={p.id} className={`p-4 border relative ${!p.active ? "opacity-60" : ""}`} data-testid={`preparer-card-${p.id}`}>
                <div className="flex items-start gap-3">
                  {p.avatar_url ? (
                    <img src={p.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[color:var(--brand-sky)]/15 text-[color:var(--brand-sky)] font-bold flex items-center justify-center">
                      {(p.first_name[0] || "?") + (p.last_name[0] || "")}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[color:var(--brand-navy)] truncate">{p.first_name} {p.last_name}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{p.role || "Préparateur"}</div>
                    {!p.active && <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mt-1">Inactif</div>}
                  </div>
                </div>
                <div className="flex gap-1 mt-3">
                  <Button variant="outline" size="sm" onClick={() => setPreparerEdit(p)}><Save className="w-3 h-3 mr-1" />Éditer</Button>
                  <Button variant="outline" size="sm" onClick={() => deletePreparer(p.id)} className="text-red-600"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>

      <div className="sticky bottom-0 py-4 bg-gradient-to-t from-[hsl(var(--background))] to-transparent">
        <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-navy)] w-full md:w-auto" data-testid="save-pickup-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Enregistrer la configuration</>}
        </Button>
      </div>

      {preparerEdit && <PreparerModal p={preparerEdit} onChange={setPreparerEdit} onSave={savePreparer} onClose={() => setPreparerEdit(null)} />}
    </div>
  );
}

function LocationEditor({ loc, idx, onChange, onRemove }) {
  const setHours = (dayKey, ranges) => {
    onChange(idx, { hours: { ...loc.hours, [dayKey]: ranges } });
  };
  return (
    <Card className="p-4 border-2 space-y-3" data-testid={`location-editor-${loc.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-1">
          <MapPin className="w-4 h-4 text-[color:var(--brand-sky)]" />
          <Input value={loc.name} onChange={e => onChange(idx, { name: e.target.value })} placeholder="Nom du point (ex: Boutique centre-ville)" className="font-semibold" data-testid={`loc-name-${idx}`} />
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase">Actif</span>
            <Switch checked={loc.active !== false} onCheckedChange={v => onChange(idx, { active: v })} />
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => onRemove(idx)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
      </div>
      <div className="grid md:grid-cols-4 gap-2">
        <Input value={loc.address || ""} onChange={e => onChange(idx, { address: e.target.value })} placeholder="Adresse (rue)" className="md:col-span-2" />
        <Input value={loc.postal_code || ""} onChange={e => onChange(idx, { postal_code: e.target.value })} placeholder="Code postal" />
        <Input value={loc.city || ""} onChange={e => onChange(idx, { city: e.target.value })} placeholder="Ville" />
      </div>
      <Textarea value={loc.notes || ""} onChange={e => onChange(idx, { notes: e.target.value })} placeholder="Instructions de retrait (horaires spéciaux, étage, digicode…)" rows={2} />

      <div>
        <div className="text-[11px] uppercase tracking-wider font-bold text-[color:var(--brand-navy)] mb-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Horaires d'ouverture</div>
        <div className="grid gap-2">
          {DAYS.map(d => {
            const ranges = loc.hours?.[d.key] || [];
            return (
              <div key={d.key} className="flex items-center gap-2 text-sm">
                <span className="w-10 text-xs font-bold uppercase text-[hsl(var(--muted-foreground))]">{d.label}</span>
                {ranges.length === 0 ? (
                  <button type="button" onClick={() => setHours(d.key, [{ open: "09:00", close: "18:00" }])} className="text-xs text-[color:var(--brand-sky)] font-semibold hover:underline">+ Ouvrir</button>
                ) : (
                  <div className="flex flex-wrap gap-2 flex-1">
                    {ranges.map((r, i) => (
                      <div key={i} className="flex items-center gap-1 px-2 py-1 bg-[hsl(var(--muted))]/30 rounded-lg text-xs">
                        <Input type="time" value={r.open} onChange={e => setHours(d.key, ranges.map((x, j) => j === i ? { ...x, open: e.target.value } : x))} className="h-7 w-24 text-xs" />
                        <span>→</span>
                        <Input type="time" value={r.close} onChange={e => setHours(d.key, ranges.map((x, j) => j === i ? { ...x, close: e.target.value } : x))} className="h-7 w-24 text-xs" />
                        <button type="button" onClick={() => setHours(d.key, ranges.filter((_, j) => j !== i))} className="text-red-500 hover:bg-red-50 rounded p-0.5"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    <button type="button" onClick={() => setHours(d.key, [...ranges, { open: "14:00", close: "18:00" }])} className="text-xs text-[color:var(--brand-sky)] font-semibold hover:underline">+ plage</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wider font-bold text-[color:var(--brand-navy)] mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Jours fermés (exceptionnels)</div>
        <Input
          value={(loc.closed_days || []).join(", ")}
          onChange={e => onChange(idx, { closed_days: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })}
          placeholder="ex: 2026-05-01, 2026-07-14"
          className="text-sm font-mono"
        />
        <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">Format ISO YYYY-MM-DD, séparés par virgules.</p>
      </div>
    </Card>
  );
}

function PreparerModal({ p, onChange, onSave, onClose }) {
  return (
    <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="preparer-modal">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-full bg-[color:var(--brand-sky)]/15 flex items-center justify-center">
            <Users className="w-6 h-6 text-[color:var(--brand-sky)]" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-bold text-lg">Préparateur</h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Apparaît dans le suivi des commandes (avatar + prénom/nom).</p>
          </div>
          <button onClick={onClose} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Prénom</Label><Input value={p.first_name} onChange={e => onChange({ ...p, first_name: e.target.value })} data-testid="preparer-first-name" /></div>
          <div><Label>Nom</Label><Input value={p.last_name} onChange={e => onChange({ ...p, last_name: e.target.value })} data-testid="preparer-last-name" /></div>
        </div>
        <div><Label>Rôle / fonction</Label><Input value={p.role || ""} onChange={e => onChange({ ...p, role: e.target.value })} placeholder="ex: Préparateur, Manager…" /></div>
        <div>
          <Label>Photo / avatar</Label>
          <div className="flex items-center gap-3 mt-1.5">
            {p.avatar_url ? (
              <img src={p.avatar_url} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-[color:var(--brand-sky)]/30" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-[color:var(--brand-sky)]/10 flex items-center justify-center text-[color:var(--brand-sky)] font-bold text-xl">
                {(p.first_name[0] || "?") + (p.last_name[0] || "")}
              </div>
            )}
            <div className="flex-1 space-y-2">
              <ImageUploader folder="team" label="Uploader une photo" onUploaded={(url) => onChange({ ...p, avatar_url: url })} />
              {p.avatar_url && (
                <button type="button" onClick={() => onChange({ ...p, avatar_url: "" })} className="text-xs text-red-600 hover:underline">Retirer</button>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={p.active !== false} onCheckedChange={v => onChange({ ...p, active: v })} />
          <span className="text-sm">Actif (disponible pour assignation)</span>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={onSave} className="bg-[color:var(--brand-navy)]" data-testid="preparer-save">
            <Save className="w-4 h-4 mr-1" />Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}
